import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowTriangleIcon, CurecallBadge, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { getFullName } from 'src/utils/string-formatter.util'
import type { THumanReviewListItem } from './human-review-requests.type'

type Props = {
  label: string
  items: THumanReviewListItem[]
  selectedPatientMedEventId: number | null
  onSelect: (patientMedEventId: number) => void
}

const HumanReviewPatientDropdown = ({ label, items, selectedPatientMedEventId, onSelect }: Props) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (items.some((listItem) => listItem.patientMedEventId === selectedPatientMedEventId)) setIsOpen(true)
  }, [items, selectedPatientMedEventId])

  return (
    <details open={isOpen}>
      <summary
        className="c-sticky c-top-0 c-px-4 c-flex c-items-center c-justify-between c-border-b c-border-b-grey-200 c-shadow-[0px_-1px_0px_0px_#D9DDE5] hover:c-bg-grey-100 c-cursor-pointer c-bg-white"
        onClick={(event) => {
          event.preventDefault()
          setIsOpen((prev) => !prev)
        }}
      >
        <span className="c-py-2 c-truncate c-font-nexa c-font-black">{label}</span>
        <ArrowTriangleIcon className="c-h-4 c-w-4" position={isOpen ? 'UP' : 'DOWN'} />
      </summary>
      <ul className="c-p-0 c-list-none c-my-0">
        {items.map((listItem) => {
          const isSelected = listItem.patientMedEventId === selectedPatientMedEventId
          const isResolved = listItem.resolution !== null
          return (
            <li
              key={listItem.patientMedEventId}
              onClick={() => onSelect(listItem.patientMedEventId)}
              className={twMerge(
                'c-flex c-items-center c-gap-4 c-justify-between c-border-b c-border-b-grey-200 c-cursor-pointer hover:c-bg-secondary-100 c-px-4 c-h-[38px] c-font-thin',
                isSelected &&
                  'c-h-[70px] c-text-secondary-800 c-font-bold c-bg-secondary-100 c-border-r-4 c-border-r-secondary-800',
                isResolved && 'hover:c-bg-green-100',
                isSelected && isResolved && 'c-bg-green-100 c-text-grey-700 c-border-r-green-700',
              )}
            >
              <span className="c-truncate">
                {getFullName({
                  firstname: listItem.patient.firstName ?? '',
                  lastname: listItem.patient.lastName ?? '',
                })}
              </span>
              {isResolved && (
                <CurecallBadge color={ECurecallBadgeColor.GREEN}>
                  {t('components.patient-med-event-overview.reviewed')}
                </CurecallBadge>
              )}
            </li>
          )
        })}
      </ul>
    </details>
  )
}

export default HumanReviewPatientDropdown
